import { Head, Link, usePage } from "@inertiajs/react"
import { Coffee, Plus, Image as ImageIcon, Mic, ArrowUp } from "lucide-react"
import { useState } from "react"

import { signUpPath, signInPath, dashboardPath } from "@/routes"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

interface Message {
  role: "user" | "assistant"
  content: string
}

export default function ChatIndex() {
  const page = usePage()
  const { auth } = page.props as {
    auth?: { user?: { id: number; name: string; email: string } }
  }
  const [message, setMessage] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])

  const handleSendMessage = async () => {
    if (!message.trim() || isLoading) return

    setIsLoading(true)
    const userMessage = message
    setMessage("")

    const newUserMessage: Message = { role: "user", content: userMessage }
    setMessages((prev) => [...prev, newUserMessage])

    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute("content") || "",
        },
        body: JSON.stringify({
          message: userMessage,
          conversation_history: messages,
        }),
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error("Server error:", errorText)
        throw new Error(`Server responded with ${response.status}`)
      }

      const data = await response.json()

      if (data.response) {
        const assistantMessage: Message = {
          role: "assistant",
          content: data.response,
        }
        setMessages((prev) => [...prev, assistantMessage])
      }
    } catch (error) {
      console.error("Error sending message:", error)
      setMessage(userMessage)
      setMessages((prev) => prev.slice(0, -1))
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const suggestedQuestions = [
    {
      id: "arabica-robusta",
      text: "¿Cuál es la diferencia entre café Arábica y Robusta?",
    },
    {
      id: "sca-grading",
      text: "¿Cómo se califica un café especial según la SCA?",
    },
    {
      id: "coffee-regions",
      text: "¿Cuáles son las mejores regiones productoras de café?",
    },
    {
      id: "green-coffee-price",
      text: "¿Cuánto cuesta el café verde en el mercado actual?",
    },
    {
      id: "fermentation-process",
      text: "¿Qué es el proceso de fermentación en café?",
    },
    {
      id: "sustainability-certifications",
      text: "¿Qué certificaciones existen para café sostenible?",
    },
  ]

  return (
    <>
      <Head title="Catador Pro - Asistente de Café" />

      <div className="bg-background min-h-screen">
        {/* Header */}
        <header className="border-b">
          <div className="container mx-auto flex h-14 items-center justify-between px-4">
            <div className="flex items-center gap-2">
              <Coffee className="text-primary h-5 w-5" />
              <span className="font-serif text-lg font-semibold">
                Catador Pro
              </span>
            </div>
            <div>
              {auth?.user ? (
                <Link href={dashboardPath()}>
                  <Button size="sm" variant="outline">
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <div className="flex gap-2">
                  <Link href={signInPath()}>
                    <Button size="sm" variant="outline">
                      Iniciar sesión
                    </Button>
                  </Link>
                  <Link href={signUpPath()}>
                    <Button size="sm">Registrarse</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="container mx-auto max-w-4xl px-4 py-8">
          {/* Chat Section */}
          <div className="mb-12">
            {messages.length === 0 ? (
              <>
                <div className="mb-8 flex items-center justify-center gap-2">
                  <Coffee className="text-primary h-6 w-6" />
                  <h1 className="font-serif text-2xl font-semibold">
                    ¿Qué quieres saber sobre Café?
                  </h1>
                </div>

                {/* Suggested Questions */}
                <div className="mb-6 flex flex-wrap justify-center gap-3">
                  {suggestedQuestions.map((question) => (
                    <Link
                      key={question.id}
                      href={`/chat/${question.id}`}
                      className="bg-card hover:bg-accent hover:text-primary rounded-full border px-4 py-2 text-sm transition-colors"
                    >
                      {question.text}
                    </Link>
                  ))}
                </div>
              </>
            ) : (
              <div className="mb-6 space-y-4">
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        msg.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">
                        {msg.content}
                      </p>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <div className="bg-muted max-w-[80%] rounded-2xl px-4 py-3">
                      <p className="text-muted-foreground text-sm">
                        Escribiendo...
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Input Box */}
            <div className="relative mx-auto max-w-3xl">
              <div className="bg-card rounded-3xl border shadow-sm">
                <div className="flex items-center gap-2 p-4">
                  <Input
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="¿Cómo puedo ayudarte hoy?"
                    className="flex-1 border-0 bg-transparent focus-visible:ring-0"
                    disabled={isLoading}
                  />
                  <div className="flex items-center gap-2">
                    <Button size="icon" variant="ghost" className="h-8 w-8">
                      <Plus className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8">
                      <ImageIcon className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8">
                      <Mic className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      className="h-8 w-8 rounded-full"
                      disabled={!message.trim() || isLoading}
                      onClick={handleSendMessage}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <p className="text-muted-foreground mt-2 text-center text-xs">
                Catador Pro puede cometer errores. Verifica información
                importante.
              </p>
            </div>
          </div>
        </main>
      </div>
    </>
  )
}
